// Spelling tolerance, tried only after every exact and phrase rule has found
// nothing. A distance here is a count of edits, not a probability, and a
// near miss that could belong to two aisles belongs to neither: the item
// stays in Other. The user's text is never corrected on screen.
//
// How much is forgiven depends on the word's length, because one changed
// letter turns a short real word into another: wipes into wines, stick into
// stock, clips into chips (g80). A slip of the fingers, meaning a missing,
// extra or swapped letter, rarely does.

import type { CategoryId } from "./categories.js";

/** Longest query worth comparing: one word, or a head-ending pair of words. */
export const MAX_FUZZY_LENGTH = 24;

/**
 * Optimal string alignment distance: insertions, deletions, substitutions and
 * the swap of two neighbouring letters each cost one. Returns max + 1 as soon
 * as the answer is known to exceed max, so a long mismatch costs little.
 */
export function osaDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let before: number[] = [];
  let previous: number[] = [];
  for (let j = 0; j <= b.length; j += 1) previous.push(j);
  for (let i = 1; i <= a.length; i += 1) {
    const row: number[] = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let value = Math.min((previous[j] as number) + 1, (row[j - 1] as number) + 1, (previous[j - 1] as number) + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, (before[j - 2] as number) + 1);
      }
      row.push(value);
      if (value < rowMin) rowMin = value;
    }
    if (rowMin > max) return max + 1;
    before = previous;
    previous = row;
  }
  return Math.min(previous[b.length] as number, max + 1);
}

/** True when b is a with exactly one pair of neighbouring letters swapped. */
export function isSwap(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  const differ: number[] = [];
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) differ.push(i);
    if (differ.length > 2) return false;
  }
  if (differ.length !== 2) return false;
  const [i, j] = differ as [number, number];
  return j === i + 1 && a[i] === b[j] && a[j] === b[i];
}

/** True when b is a with one letter missing, one extra, or one neighbouring pair swapped. */
export function isSlip(a: string, b: string): boolean {
  if (a.length === b.length) return isSwap(a, b);
  // One edit between strings of different lengths can only add or drop a letter.
  return osaDistance(a, b, 1) === 1;
}

/**
 * What a query of this length may differ from an alias by:
 *
 * - "none", three letters or fewer: too short to guess at;
 * - "swap", four letters: two neighbouring letters swapped ("mlik"), since
 *   anything more turns "lime" into "line";
 * - "slip", five to seven letters: a missing, extra or swapped letter
 *   ("tomatos", "bananna"), but not a changed one;
 * - "any", eight letters or more: two edits of any kind, so "brocolli" still
 *   reaches broccoli.
 */
export type Band = "none" | "swap" | "slip" | "any";

export function band(length: number): Band {
  if (length <= 3) return "none";
  if (length === 4) return "swap";
  if (length <= 7) return "slip";
  return "any";
}

export interface FuzzyEntry {
  readonly alias: string;
  readonly category: CategoryId;
}

/** Aliases filed by first letter and length, so a query compares against few. */
export class FuzzyIndex {
  private readonly buckets = new Map<string, FuzzyEntry[]>();

  constructor(entries: Iterable<FuzzyEntry>) {
    for (const entry of entries) {
      const key = `${entry.alias[0]}:${entry.alias.length}`;
      const bucket = this.buckets.get(key);
      if (bucket) bucket.push(entry);
      else this.buckets.set(key, [entry]);
    }
  }

  candidates(first: string, length: number): readonly FuzzyEntry[] {
    return this.buckets.get(`${first}:${length}`) ?? [];
  }
}

export interface FuzzyHit {
  readonly category: CategoryId;
  readonly alias: string;
  readonly distance: number;
}

/**
 * The one category a query is closest to, or null. The first letter must
 * agree. A category wins only when it is strictly closer than every other.
 * A pair of words is allowed only what its shorter word would be, so both
 * edits cannot land in one short word ("birthday card" is not "birthday cake").
 */
export function fuzzyMatch(query: string, index: FuzzyIndex): FuzzyHit | null {
  if (!/^[a-z]+(?: [a-z]+)?$/.test(query) || query.length > MAX_FUZZY_LENGTH) return null;
  const shortest = Math.min(...query.split(" ").map((word) => word.length));
  const rule = band(shortest);
  if (rule === "none") return null;
  const max = rule === "any" ? 2 : 1;
  const reach = rule === "swap" ? 0 : max;
  const lengths: number[] = [];
  for (let length = query.length - reach; length <= query.length + reach; length += 1) lengths.push(length);

  const best = new Map<CategoryId, FuzzyHit>();
  for (const length of lengths) {
    for (const entry of index.candidates(query[0] as string, length)) {
      let distance: number;
      if (rule === "any") distance = osaDistance(query, entry.alias, max);
      else if (rule === "slip") distance = isSlip(query, entry.alias) ? 1 : max + 1;
      else distance = isSwap(query, entry.alias) ? 1 : max + 1;
      if (distance > max) continue;
      const held = best.get(entry.category);
      if (!held || distance < held.distance) best.set(entry.category, { ...entry, distance });
    }
  }

  let winner: FuzzyHit | null = null;
  let runnerUp = Number.POSITIVE_INFINITY;
  for (const hit of best.values()) {
    if (winner === null || hit.distance < winner.distance) {
      if (winner !== null) runnerUp = winner.distance;
      winner = hit;
    } else if (hit.distance < runnerUp) {
      runnerUp = hit.distance;
    }
  }
  if (winner === null) return null;
  const separated = runnerUp > winner.distance;
  return separated ? winner : null;
}
